// Libs
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Link from '@mui/material/Link';

// Components
import Spinner from '../../../components/Spinner';
import Error from '../../../components/Error';
import GridCard from '../../../components/Products/Card/GridCard';
import GridButton from '../../../components/Button/GridButton';
import ListButton from '../../../components/Button/ListButton';
import Order from '../../../components/Button/ReorderButton/ReorderButton';
import CartCard from '../../../components/Cart/ShippingDetails/CartCard';
import KitsButton from '../../../components/Kits/Buttons/KitsButton';
import IconButton from '../../../components/Button/IconButton/IconButton';
import Feather from '../../../components/Feather';
import SharedKitProductQtyButtons from '../../../components/Kits/SharedKitProductQtyButtons/SharedKitProductQtyButtons';

// Hooks
import useUI from '../../../hooks/useUI';
import useSharedKitActions from '../hooks/useSharedKitActions';
import useSharedKitOrder from '../hooks/useSharedKitOrder';
import useSharedKitForm from '../hooks/useSharedKitForm';
import useSharedKitProducts from '../hooks/useSharedKitProducts';

// Actions
import {
  GetSharedKitDetails,
  ClearSharedKitDetails,
  SetSharedKitDetails,
} from '../../../actions/sharedKits';

// Selectors
import { productsObjSelector } from '../../../store/selectors';

// Constants
import {
  ORDER_BUTTON_TITLE,
  SHARED_KITS_PATH,
  EDIT_SHARED_KIT_BUTTON,
} from '../../../constants/sharedKits';

// Styles
import { useStyles } from './SharedKitDetailsStyles';

const PRODUCT_IMAGE_HEIGHT_DESKTOP = 250;
const PRODUCT_IMAGE_HEIGHT_TABLET = 200;
const PRODUCT_IMAGE_HEIGHT_MOBILE = 138;

const SharedKitDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { kitId } = useParams();
  const { isDesktopMode, isTabletMode, isMobileMode } = useUI();
  const [listView, setListView] = useState(true);
  const { sharedKits, kit, productsObj, apiError, actionError, isLoading } =
    useSelector((state) => ({
      sharedKits: state.sharedKits.data,
      kit: state.sharedKits.currentKit,
      productsObj: productsObjSelector(state),
      apiError: state.apiError?.['getSharedKitDetails'],
      actionError: state.sharedKits.actionError,
      isLoading: state.isLoading,
    }));
  const { canOrder } = useSharedKitActions(kit);
  const { onOrderClick } = useSharedKitOrder();
  const { onEditSharedKit } = useSharedKitForm();
  const { removeProduct } = useSharedKitProducts();
  const isOwner = !!kit?.is_owner;
  const { classes } = useStyles({ listView, isMobileMode });

  useEffect(() => {
    const localKit = sharedKits?.find((item) => item.id === +kitId);

    if (kitId && localKit) {
      dispatch(SetSharedKitDetails(localKit));
    }
    if (kitId && !localKit) {
      dispatch(GetSharedKitDetails(kitId));
    }

    return () => {
      dispatch(ClearSharedKitDetails());
    };
  }, [dispatch, kitId, sharedKits]);

  const breadcrumbs = [
    <Link
      underline="none"
      key="1"
      color="inherit"
      className={classes.breadcrumbLink}
      onClick={() => navigate(SHARED_KITS_PATH)}>
      Shared Marketing Kits
    </Link>,
    <Link key="2" color="inherit" underline="none">
      Kit Details
    </Link>,
  ];

  if (isLoading) {
    return <Spinner />;
  }

  if (apiError) {
    return <Error message={apiError} />;
  }

  if (!kit) {
    return null;
  }

  const renderProducts = () => {
    return (
      <>
        {kit?.products.length > 0 && (
          <Box className={classes.productsLayout}>
            {kit?.products.map((item) =>
              listView ? (
                <CartCard
                  key={item.product}
                  product={productsObj?.[item.product]}
                  kit={kit}
                  nums={item.nums}
                  kitDetailsPage
                  readOnly={!isOwner}
                  KitQtyComponent={SharedKitProductQtyButtons}
                  onDeleteKitProduct={removeProduct}
                  kitItemOrderable={item.isOrderable}
                />
              ) : (
                <GridCard
                  key={item.product}
                  product={productsObj?.[item.product]}
                  kit={kit}
                  nums={item.nums}
                  isTabletMode={isTabletMode}
                  readOnly={!isOwner}
                  KitQtyComponent={SharedKitProductQtyButtons}
                  onDeleteKitProduct={removeProduct}
                  kitItemOrderable={item.isOrderable}
                  imageContainer={{
                    desktop: ['100%', PRODUCT_IMAGE_HEIGHT_DESKTOP],
                    tablet: ['100%', PRODUCT_IMAGE_HEIGHT_TABLET],
                    mobile: ['100%', PRODUCT_IMAGE_HEIGHT_MOBILE],
                  }}
                />
              ),
            )}
          </Box>
        )}
      </>
    );
  };
  const renderBreadcrumbs = () => {
    return isDesktopMode ? (
      <Breadcrumbs
        className={classes.breadCrumbs}
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        mt={3}>
        {breadcrumbs}
      </Breadcrumbs>
    ) : (
      <Typography
        variant={isMobileMode ? 'h1' : 'h2'}
        mt={isMobileMode ? '20px' : '40px'}>
        Kit Details
      </Typography>
    );
  };
  return (
    <div className={classes.kitDetails}>
      {renderBreadcrumbs()}
      <Box
        display={'flex'}
        flexDirection={isMobileMode ? 'column' : 'row'}
        justifyContent={'space-between'}
        mt={4}
        position={'relative'}>
        <div className={classes.titleColumn}>
          <Typography
            variant="h2"
            className={classes.kitName}
            data-testid={'shared-kit-details-name'}>
            {kit?.name}
          </Typography>
          {isOwner && (
            <IconButton
              startIcon={<Feather type={'edit'} />}
              className={classes.editIconButton}
              onClick={() => onEditSharedKit(kit)}
              data-testid={'edit-shared-kit-button'}>
              {EDIT_SHARED_KIT_BUTTON}
            </IconButton>
          )}
        </div>
        <Order
          disabled={!canOrder}
          kit={kit}
          onClick={onOrderClick}
          title={ORDER_BUTTON_TITLE}
          inTable={false}
          className={classes.kitOrderButton}
        />
      </Box>

      {isOwner && actionError && (
        <Typography
          className={classes.actionError}
          data-testid={'shared-kit-action-error'}>
          {actionError}
        </Typography>
      )}

      <Box mt={4} mb={4}>
        <Box
          display={'flex'}
          justifyContent={'space-between'}
          mb={2}
          alignItems={'center'}>
          <Box display={'flex'} alignItems={'center'} gap={2}>
            <Typography className={classes.productsLabel} variant="h2">
              Products ({kit.products.length})
            </Typography>
            {isOwner && (
              <Feather
                type="containedRoundedPlus"
                onClick={() =>
                  navigate(`${SHARED_KITS_PATH}/${kit.id}/materials`)
                }
                data-testid={'add-shared-kit-products-button'}
              />
            )}
          </Box>
          <Box display={'flex'} gap={2}>
            <ListButton
              isActive={listView}
              onClick={() => setListView(true)}></ListButton>
            <GridButton
              isActive={!listView}
              onClick={() => setListView(false)}></GridButton>
          </Box>
        </Box>
        {renderProducts()}
      </Box>
      {isTabletMode && (
        <Box
          flexGrow={'1'}
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'end'}>
          <KitsButton />
        </Box>
      )}
    </div>
  );
};

export default SharedKitDetails;
